if (!customElements.get('product-form')) {
    customElements.define(
        'product-form',
        class ProductForm extends HTMLElement {
            constructor() {
                super();

                this.form = this.querySelector('form');
                this.variantIdInput.disabled = false;
                this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
                this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                this.submitButton = this.querySelector('[type="submit"]');
                this.submitButtonText = this.submitButton.querySelector('span');
                this.stickyBar = document.querySelector('sticky-atc');
                this.stickyATC = this.stickyBar?.querySelector('button[type="submit"]');
                this.stickyATCtText = this.stickyATC?.querySelector('[data-atc-text]');

                if (document.querySelector('cart-drawer')) {
                    this.submitButton.setAttribute('aria-haspopup', 'dialog');
                    if (this.stickyATC) {
                        this.stickyATC.setAttribute('aria-haspopup', 'dialog');
                    }
                }

                this.hideErrors = this.dataset.hideErrors === 'true';
                document.addEventListener('click', this.shopifyPaymentButtonsHandler.bind(this), true);
            }

            shopifyPaymentButtonsHandler(evt) {
                if (evt.target.matches(".shopify-payment-button") || evt.target.closest('.shopify-payment-button')) {
                    // A variant change starts an async section fetch; the hidden id is
                    // only correct once it resolves (the submit button carries the
                    // `disabled`/`aria-disabled` state for exactly that window). Block
                    // accelerated/wallet checkout until then so it can never carry the
                    // stale (default/lowest) variant.
                    const updating = this.submitButton.hasAttribute('disabled')
                        || this.submitButton.getAttribute('aria-disabled') === 'true';
                    if (!this.hasSelectedSize()) {
                        evt.stopPropagation();
                        this.onSubmitHandler(evt);
                    } else if (updating) {
                        evt.stopPropagation();
                        evt.preventDefault();
                    }
                }
            }

            hasSelectedSize() {
                // Only gate forms that actually have size options bound to them.
                // Size radios live in <variant-selects> (a sibling of the form) and
                // associate to it via their form= attribute, so match on .form ===
                // this.form rather than a document-wide lookup. Forms with no size —
                // the cart upsell, single-variant quick-add, complementary products —
                // are never gated; otherwise their Add to Cart is blocked wherever no
                // size picker is present (e.g. the cart page / drawer).
                const sizes = [].slice
                    .call(document.querySelectorAll('.size-wrapper input[type="radio"]'))
                    .filter((size) => size.form === this.form);

                if (!sizes.length) return true;

                const variantInput = this.variantIdInput;

                return sizes.some(function (size) {
                    return size.checked;
                }) && variantInput && !!variantInput.value;
            }

            updatePDPInCartCount(data){
                if(!location.pathname.toLowerCase().includes("/products/")) return;
                const URLSplit = location.pathname.split("/");
                const handle = URLSplit[URLSplit.length - 1].trim();
                if(data.handle !== handle) return;
                const target = document.querySelector('.product-form__quantity .quantity-cart');
                if(!target) return;
                target.innerText = data.quantity;
                document.querySelector(".quantity__rules-cart").classList.remove('hidden');
            }

            onSubmitHandler(evt) {
                evt.preventDefault();
                if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

                this.handleErrorMessage();

                const selectSizeMessage = document.querySelector('.select-size-message');

                const hasChecked = this.hasSelectedSize();

                selectSizeMessage && selectSizeMessage.classList.toggle('hidden', hasChecked);

                if (!hasChecked) {
                    selectSizeMessage && selectSizeMessage.scrollIntoView({
                        block: 'center',
                        inline: 'center',
                        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                    });
                    return;
                }

                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.classList.add('loading');
                this.querySelector('.loading__spinner').classList.remove('hidden');
                if (this.stickyATC) {
                    this.stickyATC.setAttribute('aria-disabled', true);
                    this.stickyATC.classList.add('loading');
                    this.stickyATC.querySelector('.loading__spinner').classList.remove('hidden');
                }

                const config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                const formData = new FormData(this.form);
                if (this.cart) {
                    formData.append(
                        'sections',
                        this.cart.getSectionsToRender().map((section) => section.id)
                    );
                    formData.append('sections_url', window.location.pathname);
                    this.cart.setActiveElement(document.activeElement);
                }
                config.body = formData;

                fetch(`${routes.cart_add_url}`, config)
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status) {
                            publish(PUB_SUB_EVENTS.cartError, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                errors: response.errors || response.description,
                                message: response.message,
                            });
                            this.handleErrorMessage(response.description);
                            // /cart/add can change the cart even when it responds with an
                            // error: requesting more than the available stock adds what's
                            // left and then reports "you can only add N". Re-sync the
                            // mini-cart from the server so it reflects the real cart.
                            this.refreshMiniCart(formData.get('id'));

                            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                            if (!soldOutMessage) return;
                            this.submitButton.setAttribute('aria-disabled', true);
                            this.submitButtonText.classList.add('hidden');
                            soldOutMessage.classList.remove('hidden');
                            this.error = true;
                            return;
                        } else if (!this.cart) {
                            window.location = window.routes.cart_url;
                            return;
                        }

                        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
                        if (!this.error)
                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                cartData: response,
                            }).then(() => {
                                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                            });
                        this.error = false;
                        const quickAddModal = this.closest('quick-add-modal');
                        if (quickAddModal) {
                            document.body.addEventListener(
                                'modalClosed',
                                () => {
                                    setTimeout(() => {
                                        CartPerformance.measure("add:paint-updated-sections", () => {
                                            this.cart.renderContents(response);
                                        });
                                    });
                                },
                                {once: true}
                            );
                            quickAddModal.hide(true);
                        } else {
                            CartPerformance.measure("add:paint-updated-sections", () => {
                                this.cart.renderContents(response);
                                this.updatePDPInCartCount(response);
                            });
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                    })
                    .finally(() => {
                        this.submitButton.classList.remove('loading');
                        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
                        this.querySelector('.loading__spinner').classList.add('hidden');

                        if (this.stickyATC) {
                            this.stickyATC.classList.remove('loading');
                            if (!this.error) this.stickyATC.removeAttribute('aria-disabled');
                            this.stickyATC.querySelector('.loading__spinner').classList.add('hidden');
                        }

                        CartPerformance.measureFromEvent("add:user-action", evt);
                    });
            }

            refreshMiniCart(variantId) {
                // Re-render the mini-cart sections so the icon bubble and drawer
                // contents match the server cart, and open the drawer when items
                // actually went in despite the error.
                if (!this.cart) return;

                // The icon bubble hasn't been re-rendered on the error path yet,
                // so it still shows the pre-add count — capture it to tell a
                // partial add (cart grew) from a pure failure (e.g. sold out).
                const previousCount = this.getCartIconCount();

                const sectionsToRender = this.cart.getSectionsToRender();
                const sectionIds = sectionsToRender.map((section) => section.id);
                Promise.all([
                    fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`).then((response) =>
                        response.json()
                    ),
                    fetch(`${routes.cart_url}.js`).then((response) => response.json()),
                ])
                    .then(([sections, cart]) => {
                        sectionsToRender.forEach((section) => {
                            const sectionElement = section.selector
                                ? document.querySelector(section.selector)
                                : document.getElementById(section.id);
                            if (!sectionElement || !sections[section.id]) return;
                            sectionElement.innerHTML = this.cart.getSectionInnerHTML(
                                sections[section.id],
                                section.selector
                            );
                        });
                        // The drawer overlay is inside the replaced section — re-bind
                        // its click-to-close, as renderContents does.
                        const overlay = this.cart.querySelector('#CartDrawer-Overlay');
                        if (overlay) overlay.addEventListener('click', this.cart.close.bind(this.cart));

                        this.cart.classList.toggle('is-empty', cart.item_count === 0);
                        const line = cart.items.find((item) => String(item.variant_id) === String(variantId));
                        if (line) this.updatePDPInCartCount({handle: line.handle, quantity: line.quantity});

                        // Items were added despite the error (partial add) — surface
                        // the drawer so the shopper sees what actually went in. Only
                        // the drawer: the notification renders a specific line item
                        // we don't have a key for on this path.
                        if (cart.item_count > previousCount && this.cart.localName === 'cart-drawer') {
                            this.cart.open();
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                    });
            }

            getCartIconCount() {
                const bubbleText = document.querySelector('#cart-icon-bubble .cart-count-bubble .visually-hidden');
                if (!bubbleText) return 0;
                const match = bubbleText.textContent.match(/\d+/);
                return match ? parseInt(match[0], 10) : 0;
            }

            handleErrorMessage(errorMessage = false) {
                if (this.hideErrors) return;

                this.errorMessageWrapper =
                    this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
                if (!this.errorMessageWrapper) return;
                this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

                this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

                if (errorMessage) {
                    this.errorMessage.textContent = errorMessage;
                }
            }

            toggleSubmitButton(disable = true, text) {
                if (disable) {
                    this.submitButton.setAttribute('disabled', 'disabled');
                    if (text) this.submitButtonText.textContent = text;
                    if (this.stickyATC) {
                        this.stickyATC.setAttribute('disabled', 'disabled');
                        if (text) this.stickyATCtText.textContent = text;
                    }
                } else {
                    this.submitButton.removeAttribute('disabled');
                    this.submitButtonText.textContent = window.variantStrings.addToCart;

                    if (this.stickyATC) {
                        this.stickyATC.removeAttribute('disabled');
                        this.stickyATCtText.textContent = window.variantStrings.addToCart;
                    }
                }
            }

            get variantIdInput() {
                return this.form.querySelector('[name=id]');
            }
        }
    );
}
